import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

const SLIDES = [
  {
    url: "https://customer-assets.emergentagent.com/job_mtbl-opportunities/artifacts/bpguvbiu_9cfc6e25ad2316a133c59a51395d2cfb605997685f908aa9d8fd340be700ee48.png",
    alt: "Growth against a city skyline at sunset",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_mtbl-opportunities/artifacts/fzl1hjyw_shyb.png",
    alt: "Collaboration around a startup strategy board",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_mtbl-opportunities/artifacts/h9v70k0l_Screenshot_20260421_143604_Canva.jpg",
    alt: "Diverse executive boardroom meeting",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_mtbl-opportunities/artifacts/by7uhspr_Screenshot_20260421_143436_Canva.jpg",
    alt: "Team brainstorming behind a glass wall",
  },
  {
    url: "https://customer-assets.emergentagent.com/job_mtbl-opportunities/artifacts/h4ypep9t_Screenshot_20260421_143516_Canva.jpg",
    alt: "Team collaborating around laptops in a modern office",
  },
];

const INTERVAL_MS = 5000;

export default function HeroSlideshow() {
  const { t } = useTranslation();
  const [active, setActive] = useState(0);

  const goTo = useCallback((i) => setActive(i % SLIDES.length), []);

  useEffect(() => {
    const id = setInterval(() => {
      setActive((a) => (a + 1) % SLIDES.length);
    }, INTERVAL_MS);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    SLIDES.forEach((s) => {
      const i = new Image();
      i.src = s.url;
    });
  }, []);

  return (
    <section
      data-testid="home-landscape-slideshow"
      className="relative w-full h-[95vh] md:h-[100vh] min-h-[640px] overflow-hidden -mt-24 md:-mt-28"
    >
      {SLIDES.map((s, i) => (
        <div
          key={s.url}
          aria-hidden={i !== active}
          className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${
            i === active ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={s.url}
            alt={s.alt}
            className="absolute inset-0 w-full h-full object-cover"
            loading={i === 0 ? "eager" : "lazy"}
          />
        </div>
      ))}

      {/* Darkening gradient for text legibility */}
      <div className="absolute inset-0 bg-gradient-to-r from-[#020817]/85 via-[#020817]/55 to-[#020817]/35" />
      <div className="absolute inset-0 bg-gradient-to-t from-[#020817]/80 via-[#020817]/25 to-[#020817]/50" />

      {/* Text content */}
      <div className="relative z-10 h-full flex items-center">
        <div className="container-mtbl pt-24 md:pt-28">
          <div className="max-w-4xl">
            <p
              data-testid="slideshow-overline"
              className="text-[10px] md:text-xs font-bold uppercase tracking-[0.3em] text-white/75"
            >
              {t("hero.overline")}
            </p>

            <h1
              data-testid="slideshow-headline"
              className="mt-6 md:mt-8 text-white font-bold tracking-tighter leading-[1.02] text-4xl sm:text-5xl md:text-6xl lg:text-[64px]"
            >
              {t("hero.headlinePart1")}{" "}
              <span className="text-white/80">{t("hero.headlinePart2")}</span>{" "}
              {t("hero.headlinePart3")}
            </h1>

            <p
              data-testid="slideshow-support"
              className="mt-7 text-white/90 text-sm md:text-base leading-relaxed max-w-3xl"
            >
              {t("hero.support")}
            </p>

            <div className="mt-10 flex flex-wrap gap-4">
              <Link
                to="/opportunities"
                data-testid="slideshow-cta-opportunities"
                className="inline-flex items-center justify-center rounded-full bg-white text-[#0B1A3D] px-7 py-3.5 text-sm font-semibold tracking-wide hover:bg-slate-100 transition-colors"
              >
                {t("common.viewOpportunities")}
              </Link>
              <Link
                to="/contact"
                data-testid="slideshow-cta-partner"
                className="inline-flex items-center justify-center rounded-full border border-white/50 text-white px-7 py-3.5 text-sm font-semibold tracking-wide hover:border-white hover:bg-white/10 transition-colors"
              >
                {t("common.partnerWithUs")}
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Dot indicators */}
      <div
        className="absolute bottom-8 md:bottom-10 inset-x-0 z-10 flex justify-center gap-2"
        aria-label="Slide selectors"
      >
        {SLIDES.map((s, i) => (
          <button
            key={s.url}
            type="button"
            data-testid={`slideshow-dot-${i}`}
            onClick={() => goTo(i)}
            aria-label={`Go to slide ${i + 1}`}
            className={`h-1.5 rounded-full transition-all duration-500 ${
              i === active
                ? "bg-white w-10"
                : "bg-white/50 w-2.5 hover:bg-white/80"
            }`}
          />
        ))}
      </div>
    </section>
  );
}
